import React from 'react';
import { styled } from '@mui/material/styles';
import Dialog from '@mui/material/Dialog';
import UndoIcon from '@mui/icons-material/Undo';
import MailIcon from '@mui/icons-material/Mail';
import { PrimaryButton } from './workspaceModalParts';
import { ACCENT_BLUE, SUCCESS_GREEN, WARN_ORANGE } from './qcTheme';

// Shown when the admin resolves the last Ready item with ≥1 returned (all-approved goes to Approve Assessment); the server increments the round and emails on this same event.

const Body = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  textAlign: 'center',
  padding: '34px 34px 10px',
});

const IconBadge = styled('span')({
  width: '52px',
  height: '52px',
  borderRadius: '50%',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  background: 'rgba(230,81,0,0.1)',
  color: WARN_ORANGE,
  marginBottom: '14px',
});

const Eyebrow = styled('p')({
  fontSize: '0.7rem',
  letterSpacing: '1.2px',
  fontWeight: 700,
  color: 'rgba(0,0,0,0.5)',
  textTransform: 'uppercase',
  margin: '0 0 6px',
});

const Title = styled('h2')({
  fontSize: '1.3rem',
  fontWeight: 600,
  color: 'rgba(0,0,0,0.87)',
  margin: '0 0 8px',
});

const Lede = styled('p')({
  fontSize: '0.9rem',
  lineHeight: 1.55,
  color: 'rgba(0,0,0,0.65)',
  margin: '0 0 20px',
  maxWidth: '400px',
});

const StatsGrid = styled('div')({
  display: 'flex',
  alignItems: 'stretch',
  width: '100%',
  border: '1px solid rgba(0,0,0,0.1)',
  borderRadius: '8px',
  overflow: 'hidden',
});

const StatCell = styled('div')({
  flex: 1,
  padding: '14px 10px',
});

const StatDivider = styled('div')({
  width: '1px',
  background: 'rgba(0,0,0,0.1)',
});

const StatValue = styled('div', {
  shouldForwardProp: prop => prop !== 'tone',
})<{ tone: string }>(({ tone }) => ({
  fontSize: '1.5rem',
  fontWeight: 700,
  color: tone,
}));

const StatLabel = styled('div')({
  fontSize: '0.7rem',
  letterSpacing: '0.6px',
  fontWeight: 600,
  color: 'rgba(0,0,0,0.55)',
  textTransform: 'uppercase',
  marginTop: '2px',
});

const MailBox = styled('div')({
  display: 'flex',
  alignItems: 'flex-start',
  gap: '10px',
  textAlign: 'left',
  width: '100%',
  boxSizing: 'border-box',
  background: 'rgba(25,118,210,0.06)',
  border: '1px solid rgba(25,118,210,0.22)',
  borderRadius: '6px',
  padding: '12px 14px',
  marginTop: '16px',
});

const MailLine = styled('div')({
  fontSize: '0.8125rem',
  fontWeight: 600,
  color: 'rgba(0,0,0,0.85)',
});

const WatchLine = styled('div')({
  fontSize: '0.78rem',
  lineHeight: 1.5,
  color: 'rgba(0,0,0,0.6)',
  marginTop: '2px',
});

const Footer = styled('div')({
  display: 'flex',
  justifyContent: 'flex-end',
  padding: '20px 26px 22px',
});

export interface AdminRoundReturnedModalProps {
  open: boolean;
  returnedCount: number;
  approvedCount: number;
  roundNumber: number;
  onClose: () => void;
}

export default function AdminRoundReturnedModal({
  open, returnedCount, approvedCount, roundNumber, onClose,
}: AdminRoundReturnedModalProps): JSX.Element {
  const one = returnedCount === 1;
  return (
    <Dialog
      open={open}
      onClose={onClose}
      aria-label='QC round returned to assessor'
      PaperProps={{ sx: { width: 540, maxWidth: '100%', borderRadius: '6px' } }}
      maxWidth={false}
    >
      <Body>
        <IconBadge>
          <UndoIcon sx={{ fontSize: 26 }} />
        </IconBadge>
        <Eyebrow>Round {roundNumber}</Eyebrow>
        <Title>
          {returnedCount} QC Issue{one ? '' : 's'} returned to the assessor
        </Title>
        <Lede>
          {one
            ? 'Your review is complete for this round. The returned issue goes back to the assessor as Needs Update.'
            : 'Your review is complete for this round. The returned issues go back to the assessor as Needs Update.'}
        </Lede>
        <StatsGrid>
          <StatCell>
            <StatValue tone={WARN_ORANGE}>{returnedCount}</StatValue>
            <StatLabel>{one ? 'Issue returned' : 'Issues returned'}</StatLabel>
          </StatCell>
          <StatDivider />
          <StatCell>
            <StatValue tone={SUCCESS_GREEN}>{approvedCount}</StatValue>
            <StatLabel>Reviewed</StatLabel>
          </StatCell>
        </StatsGrid>
        <MailBox>
          <MailIcon sx={{ fontSize: 19, color: ACCENT_BLUE, flex: 'none', mt: '1px' }} />
          <div style={{ minWidth: 0 }}>
            <MailLine>The assessor has been emailed.</MailLine>
            <WatchLine>
              Reworked items will come back here as Ready for TPN Review in round {roundNumber + 1}.
            </WatchLine>
          </div>
        </MailBox>
      </Body>
      <Footer>
        <PrimaryButton type='button' onClick={onClose}>Close</PrimaryButton>
      </Footer>
    </Dialog>
  );
}
